// apps/accounts/src/pages/dashboard/MainDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../lib/pocketbaseClient';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Briefcase,
  RefreshCw,
  ArrowRight,
  PlusCircle,
  CreditCard,
  Home,
  Building2,
  Users,
  Calendar,
  Clock,
  Loader2,
  BarChart3,
  PieChart,
  Eye,
  Trophy,
  Sparkles,
  IndianRupee,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RePieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { toast } from 'sonner';

const COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899', '#f97316'];

// ─── Currency helper – returns only the formatted number ──────
const formatNumber = (amount) => {
  if (!amount && amount !== 0) return '0';
  return Number(amount).toLocaleString('en-IN');
};

// ─── Small component to display amount with ₹ icon ────────────
const Currency = ({ amount, className = '' }) => (
  <span className={`inline-flex items-center gap-0.5 ${className}`}>
    <IndianRupee className="w-4 h-4 inline-block" />
    {formatNumber(amount)}
  </span>
);

export default function MainDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    projects: 0,
    activeProjects: 0,
    collectedAmount: 0,
    projectCost: 0,
    profit: 0,
    profitMargin: 0,
  });
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [expenseBreakdown, setExpenseBreakdown] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [topProjects, setTopProjects] = useState([]);

  // ─── Fetch Dashboard Data ──────────────────────────────────────
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // 1. Get all projects (accounts_projects)
      const projects = await pb.collection('accounts_projects').getFullList({
        sort: '-created',
      });

      const projectIds = projects.map(p => p.id);
      const projectIdFilter = projectIds.length > 0
        ? projectIds.map(id => `project = "${id}"`).join(' || ')
        : 'project = ""';

      // 2. Get all project expenses
      const expenses = await pb.collection('project_expenses').getFullList({
        filter: projectIdFilter,
        sort: '-date',
      });

      // 3. Get all vendor payments
      const vendorPayments = await pb.collection('vendor_payments').getFullList({
        filter: projectIdFilter,
        sort: '-paymentDate',
        expand: 'project',
      });

      // 4. Get all client payments
      const clientPayments = await pb.collection('client_payments').getFullList({
        filter: projectIdFilter,
        sort: '-paymentDate',
        expand: 'project',
      });

      // 5. Get all investments (deposits & withdrawals)
      const investments = await pb.collection('investments').getFullList({
        filter: projectIdFilter,
        sort: '-date',
      });

      // ─── Calculations ──────────────────────────────────────────
      const totalProjects = projects.length;
      const activeProjects = projects.filter(p => p.status === 'ongoing' || p.status === 'active').length;

      const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
      const totalVendorPayments = vendorPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      const totalClientPayments = clientPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      const totalDeposits = investments.filter(i => i.type === 'deposit').reduce((sum, i) => sum + (i.amount || 0), 0);
      const totalWithdrawals = investments.filter(i => i.type === 'withdrawal').reduce((sum, i) => sum + (i.amount || 0), 0);

      const netInvestedCapital = totalDeposits - totalWithdrawals;
      const collectedAmount = totalClientPayments + netInvestedCapital;
      const projectCost = totalExpenses + totalVendorPayments;
      const profit = collectedAmount - projectCost - netInvestedCapital; // = clientPayments - projectCost
      const profitMargin = totalClientPayments > 0 ? ((profit / totalClientPayments) * 100).toFixed(1) : 0;

      setStats({
        projects: totalProjects,
        activeProjects,
        collectedAmount,
        projectCost,
        profit,
        profitMargin,
      });

      // ─── Revenue Trend (last 30 days) ──────────────────────────
      const last30Days = [];
      for (let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const dayRevenue = clientPayments
          .filter(p => p.paymentDate?.startsWith(dateStr))
          .reduce((sum, p) => sum + (p.amount || 0), 0);
        const dayExpenses = expenses
          .filter(e => e.date?.startsWith(dateStr))
          .reduce((sum, e) => sum + (e.amount || 0), 0);
        const dayVendorPayments = vendorPayments
          .filter(v => v.paymentDate?.startsWith(dateStr))
          .reduce((sum, v) => sum + (v.amount || 0), 0);
        last30Days.push({
          date: date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
          revenue: dayRevenue,
          expenses: dayExpenses + dayVendorPayments,
        });
      }
      setRevenueTrend(last30Days);

      // ─── Expense Breakdown by Category ──────────────────────────
      const categoryMap = {};
      expenses.forEach(e => {
        const cat = e.category || 'Other';
        categoryMap[cat] = (categoryMap[cat] || 0) + (e.amount || 0);
      });
      const vendorTotal = vendorPayments.reduce((sum, v) => sum + (v.amount || 0), 0);
      if (vendorTotal > 0) {
        categoryMap['Vendor Payments'] = (categoryMap['Vendor Payments'] || 0) + vendorTotal;
      }
      const breakdown = Object.entries(categoryMap).map(([name, value]) => ({
        name,
        value,
      }));
      setExpenseBreakdown(breakdown);

      // ─── Recent Transactions ────────────────────────────────────
      const transactions = clientPayments
        .slice(0, 10)
        .map(p => ({
          id: p.id,
          label: p.expand?.project?.name || 'Payment',
          type: 'payment',
          amount: p.amount || 0,
          date: p.paymentDate || p.created,
        }));
      setRecentTransactions(transactions);

      // ─── Top Projects by Revenue ────────────────────────────────
      const projectRevenue = {};
      clientPayments.forEach(p => {
        const projectId = p.project || p.projectId;
        if (projectId) {
          projectRevenue[projectId] = (projectRevenue[projectId] || 0) + (p.amount || 0);
        }
      });
      const topProjectsData = Object.entries(projectRevenue)
        .map(([id, revenue]) => {
          const project = projects.find(p => p.id === id);
          return {
            id,
            name: project?.name || 'Unknown Project',
            revenue,
          };
        })
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);
      setTopProjects(topProjectsData);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <Loader2 className="w-12 h-12 animate-spin text-amber-500" />
        <p className="text-slate-500 font-medium">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6 bg-slate-50/50 min-h-screen">
      {/* ─── Header ────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight flex items-center gap-2">
            <Home className="w-6 h-6 text-amber-500" />
            Financial Dashboard
          </h1>
          <p className="text-slate-500 mt-1 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-amber-400" />
            Complete overview of all projects
            <Badge variant="outline" className="text-xs ml-2">Admin View</Badge>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchDashboardData}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
          <Button
            className="bg-amber-500 hover:bg-amber-600 text-white"
            onClick={() => navigate('/expenses')}
          >
            <PlusCircle className="w-4 h-4 mr-1" /> Add Expense
          </Button>
        </div>
      </div>

      {/* ─── Stats Cards ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg shadow-indigo-500/10 bg-gradient-to-br from-indigo-50 to-white hover:shadow-xl transition-all hover:-translate-y-0.5 duration-200">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-indigo-600">Total Projects</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{stats.projects}</p>
                <div className="flex items-center gap-1 mt-2 text-xs text-indigo-500">
                  <Briefcase className="w-3 h-3" />
                  <span>{stats.activeProjects} active</span>
                </div>
              </div>
              <div className="bg-indigo-100 p-3 rounded-2xl">
                <Briefcase className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg shadow-emerald-500/10 bg-gradient-to-br from-emerald-50 to-white hover:shadow-xl transition-all hover:-translate-y-0.5 duration-200">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-emerald-600">Collected Amount</p>
                <p className="text-xl font-bold text-slate-800 mt-1">
                  <Currency amount={stats.collectedAmount} />
                </p>
                <div className="flex items-center gap-1 mt-2 text-xs text-emerald-500">
                  <DollarSign className="w-3 h-3" />
                  <span>Revenue + Net Invested Capital</span>
                </div>
              </div>
              <div className="bg-emerald-100 p-3 rounded-2xl">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg shadow-rose-500/10 bg-gradient-to-br from-rose-50 to-white hover:shadow-xl transition-all hover:-translate-y-0.5 duration-200">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-rose-600">Project Cost</p>
                <p className="text-xl font-bold text-slate-800 mt-1">
                  <Currency amount={stats.projectCost} />
                </p>
                <div className="flex items-center gap-1 mt-2 text-xs text-rose-500">
                  <CreditCard className="w-3 h-3" />
                  <span>Expenses + Vendor Payments</span>
                </div>
              </div>
              <div className="bg-rose-100 p-3 rounded-2xl">
                <TrendingDown className="w-6 h-6 text-rose-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`border-0 shadow-lg ${stats.profit >= 0 ? 'shadow-blue-500/10 bg-gradient-to-br from-blue-50 to-white' : 'shadow-red-500/10 bg-gradient-to-br from-red-50 to-white'} hover:shadow-xl transition-all hover:-translate-y-0.5 duration-200`}>
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className={`text-sm font-medium ${stats.profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  Profit / Loss
                </p>
                <p className={`text-xl font-bold mt-1 ${stats.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {stats.profit >= 0 ? '+' : ''}<Currency amount={stats.profit} />
                </p>
                <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                  <span>{stats.profit >= 0 ? '🟢 Profitable' : '🔴 Loss'}</span>
                  <span className="ml-1 text-slate-400">• {stats.profitMargin}% margin</span>
                </div>
              </div>
              <div className={`p-3 rounded-2xl ${stats.profit >= 0 ? 'bg-blue-100' : 'bg-red-100'}`}>
                {stats.profit >= 0 ? (
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-red-600" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ─── Charts ────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 shadow-sm border-0 hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-amber-400" />
                Revenue vs Cost (Last 30 days)
              </CardTitle>
              <Badge variant="outline" className="text-xs">Trend</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              {revenueTrend && revenueTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueTrend}>
                    <defs>
                      <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="expGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `₹${v}`} />
                    <Tooltip formatter={(v) => <Currency amount={v} />} />
                    <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} fill="url(#revGrad)" />
                    <Area type="monotone" dataKey="expenses" stroke="#f59e0b" strokeWidth={2} fill="url(#expGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  <p>No trend data available</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-0 hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="w-5 h-5 text-amber-400" />
              Expense Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72 flex items-center justify-center">
              {expenseBreakdown && expenseBreakdown.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <RePieChart>
                    <Pie
                      data={expenseBreakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {expenseBreakdown.map((_, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => <Currency amount={v} />} />
                    <Legend verticalAlign="bottom" height={36} />
                  </RePieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-slate-400 text-sm">No expense data</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ─── Top Projects & Recent Transactions ────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 shadow-sm border-0 hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" />
              Top Projects
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topProjects && topProjects.length > 0 ? (
              <div className="space-y-3">
                {topProjects.map((p, idx) => (
                  <div
                    key={p.id || idx}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition cursor-pointer group"
                    onClick={() => navigate(`/projects/${p.id}`)}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`text-sm font-bold ${idx === 0 ? 'text-amber-500' : idx === 1 ? 'text-slate-400' : 'text-slate-400'}`}>
                        #{idx + 1}
                      </span>
                      <span className="font-medium text-slate-700 truncate">{p.name || 'Project'}</span>
                    </div>
                    <span className="font-semibold text-emerald-600 text-sm whitespace-nowrap">
                      <Currency amount={p.revenue} />
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-400 text-sm">No project revenue yet</div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 shadow-sm border-0 hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-400" />
                Recent Transactions
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="text-amber-600 hover:text-amber-700"
                onClick={() => navigate('/expenses')}
              >
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {recentTransactions && recentTransactions.length > 0 ? (
              <div className="space-y-2">
                {recentTransactions.slice(0, 6).map((tx, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition rounded-lg"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`p-2 rounded-full ${tx.amount >= 0 ? 'bg-emerald-100' : 'bg-rose-100'} flex-shrink-0`}>
                        {tx.amount >= 0 ? (
                          <TrendingUp className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-rose-600" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{tx.label || 'Transaction'}</p>
                        <p className="text-xs text-slate-400">
                          {new Date(tx.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    <span className={`font-semibold text-sm whitespace-nowrap ${tx.amount >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {tx.amount >= 0 ? '+' : ''}<Currency amount={Math.abs(tx.amount)} />
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-400 text-sm">No recent transactions</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ─── Quick Actions ──────────────────────────────────────── */}
      <Card className="shadow-sm border-0 hover:shadow-lg transition-shadow">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => navigate('/expenses')}
              className="bg-amber-500 hover:bg-amber-600 text-white gap-2 shadow-md hover:shadow-lg transition-all"
            >
              <PlusCircle className="w-4 h-4" /> Add Expense
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/vendor-payments')}
              className="gap-2 border-slate-200 hover:border-slate-300"
            >
              <CreditCard className="w-4 h-4" /> Vendor Payment
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/client-payments')}
              className="gap-2 border-slate-200 hover:border-slate-300"
            >
              <DollarSign className="w-4 h-4" /> Record Payment
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/projects')}
              className="gap-2 border-slate-200 hover:border-slate-300"
            >
              <Briefcase className="w-4 h-4" /> View Projects
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}